import reactUpdate, {type Spec} from 'immutability-helper';

import {getColumnIndices} from 'notebook/integrations/chemistry/reaction-scheme/StoichiometryTableHelpers';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {BioentityTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';

import {StoichiometryStructuredTable, StoichiometryTableRow} from './StoichiometryStructuredTableAdapter';

/** A molecule created alongside the primary one (e.g. the salt-stripped parent Compound). */
export interface CreatedMoleculeRowUpdate {
  canonicalSmiles: string;
  id: string;
  name: string;
}

/**
 * Produces an updated stoichiometry table where all rows matching the molecule's `canonicalSmiles`
 * have their compound cell converted from a STRING typed value to a BIOENTITY typed
 * value referencing the newly created molecule. Row metadata (including canonicalSmiles)
 * is preserved.
 *
 * Returns null if no rows match.
 */
export default function updateStoichiometryTableWithCreatedMolecule(
  structuredTable: StoichiometryStructuredTable,
  molecule: CreatedMoleculeRowUpdate
): StoichiometryStructuredTable | null {
  const matchingRowIndices = structuredTable.rows.reduce<Array<number>>((acc, row, idx) => {
    if (row.metadata?.canonicalSmiles === molecule.canonicalSmiles) {
      acc.push(idx);
    }
    return acc;
  }, []);

  if (matchingRowIndices.length === 0) {
    return null;
  }

  const {compoundColumnIndex} = getColumnIndices(structuredTable.columns);
  const typedValue: BioentityTypedValue = {
    type: BaseTypedValueType.BIOENTITY,
    bioentitySummary: {id: molecule.id, name: molecule.name},
  };

  const rowsUpdateSpec: Record<number, object> = {};
  for (const rowIndex of matchingRowIndices) {
    const row = structuredTable.rows[rowIndex];
    const rowUpdate: Spec<StoichiometryTableRow> = {
      cells: {
        [compoundColumnIndex]: {
          text: {$set: molecule.name},
          typedValue: {$set: typedValue},
        },
      } satisfies Spec<StoichiometryTableRow['cells']>,
    };
    if (row.metadata?.drawnMolfile != null) {
      rowUpdate.metadata = {$merge: {drawnMolfileMoleculeId: molecule.id}};
    }
    rowsUpdateSpec[rowIndex] = rowUpdate;
  }

  return reactUpdate(structuredTable, {rows: rowsUpdateSpec});
}

/**
 * Applies the newly created molecule to the table, and — when creating a Form molecule also created
 * its parent Compound — applies the parent as well so both the Form row and the Compound row are
 * upgraded from unrecognized STRING cells to BIOENTITY chips in a single update.
 *
 * Returns null if neither the molecule nor its parent matches any row (nothing to update).
 */
export function updateStoichiometryTableWithCreatedMoleculeAndParent(
  structuredTable: StoichiometryStructuredTable,
  molecule: CreatedMoleculeRowUpdate,
  parentMolecule?: CreatedMoleculeRowUpdate
): StoichiometryStructuredTable | null {
  let updatedTable = updateStoichiometryTableWithCreatedMolecule(structuredTable, molecule);
  if (parentMolecule) {
    updatedTable =
      updateStoichiometryTableWithCreatedMolecule(updatedTable ?? structuredTable, parentMolecule) ??
      updatedTable;
  }
  return updatedTable;
}
